import { useEffect, useState } from "react"
import DrawerAccordion from "../../../../../../../../shared/components/data-display/drawer-accordion/DrawerAccordion"
import cx from "classnames"
import "./Payments.css"
import { PrimaryButton } from "src/pages/home/room-detail/sections/elements/Elements"
import { useNavigate } from "react-router-dom"
import { useRoom } from "src/pages/home/room-detail/hooks"
import { ItemMultiplePayment, ItemPayment } from "src/pages/home/room-detail/sections/items/Items"
import { useQuery } from "@apollo/client"
import { GET_ROOM } from "src/pages/home/graphql/queries/rooms.query"
import { EstadosOrdenHistorial, Habitacion, ModalidadGrupoTarifa, OrigenOrden, Pago, Renta, Tarifa, TiposAlojamientos } from "src/gql/schema"
import { TIPOS_EXTRAS } from "src/constants/payments"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { useProfile } from "src/shared/hooks/useProfile"
import { add, sum } from "src/shared/helpers/calculator"
import { getCardLabel } from "src/shared/sections/payment/helpers/card"
import { useData } from "../roomService/hooks/data"
import useIsColaboradorActive from "src/shared/hooks/useIsColaboradorActive"
import { useDate } from "src/shared/hooks/useDate"
import { RoleNames } from "src/shared/hooks/useAuth"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { usePrintTicket } from "src/shared/hooks/print"
import EstanciaDetailList from "./sections/EstanciaDetailList/EstanciaDetailList"
import ExtraDetailList from "./sections/ExtraDetailList/ExtraDetailList"
import PropinaDetails from "./sections/PropinaDetails/PropinaDetails"
import PagoPendienteDetails from "./sections/PagoPendienteDetails/PagoPendienteDetails"
import DescuentoDetails from "./sections/DescuentoDetails/DescuentoDetails"
import { removeObjectDuplicates } from "src/utils/array"
import useTotalPorPagar from "./hooks/useTotalPorPagar"
import useDescuentos from "./hooks/useDescuentos"

const calcularEstanciasBase = (
    tarifas: Tarifa[],
    fecha_inicio: string,
    fecha_fin: string
): number => {
    const tarifaBase = tarifas.find(
        (t) => t.grupo_tarifa?.modalidad === ModalidadGrupoTarifa.Base
    )

    const duracion = tarifaBase?.grupo_tarifa?.duracion ?? 24
    if (!duracion) return 0

    const diffHoras =
        (new Date(fecha_fin).getTime() - new Date(fecha_inicio).getTime()) /
        (1000 * 60 * 60)

    return Math.floor(diffHoras / duracion)
}

const mapTiposExtrasIcon = {
    [TIPOS_EXTRAS.persona]: "UserParentFill",
    [TIPOS_EXTRAS.hospedaje]: "MoonFill",
    [TIPOS_EXTRAS.hora]: "Clock",
}

const mapTiposExtrasTitulo = {
    [TIPOS_EXTRAS.persona]: "Personas extras",
    [TIPOS_EXTRAS.hospedaje]: "Noches extras",
    [TIPOS_EXTRAS.hora]: "Horas extras",
}

const Payments = () => {
    const navigate = useNavigate()
    const room = useRoom()
    const { usuario_id, rolName } = useProfile()
    const { getOrdersInfo } = useData()
    const { InactiveModal, validateIsColabActive } = useIsColaboradorActive()
    const { UTCStringToLocalDate } = useDate()
    const { formatCustomDate } = useFormatDate()

    const { handlePrint } = usePrintTicket()

    const { data } = useQuery<{ habitacion: Habitacion }>(GET_ROOM, {
        variables: { habitacion_id: room?.habitacion_id, usuario_id: usuario_id },
        fetchPolicy: "no-cache",
    })

    const reserva = data?.habitacion?.ultima_renta?.reserva
    const ordenesAll = data?.habitacion?.ultima_renta?.ordenes || []

    const [totalPagado, setTotalPagado] = useState<number>(0)
    const [ordenesPendientesState, setOrdenesPendientesState] = useState<any[]>([])

    const {
        ordenesPendientes,
        totalPropinas: totalPropinasOrdenes,
    } = getOrdersInfo(ordenesAll)

    const totalPorPagar = useTotalPorPagar(data?.habitacion.ultima_renta)

    const extrasPendientes =
        data?.habitacion.ultima_renta?.extras
            ?.filter((extra) => !extra.fecha_pago)
            .reduce((acc, extra) => acc + (extra.monto_extra || 0), 0) || 0

    const estanciaPendiente =
        data?.habitacion.ultima_renta?.conceptos_pendientes?.estancia_pendiente?.precio
        ?? Math.max(0, (data?.habitacion.ultima_renta?.saldo_pendiente_estancia ?? 0) - extrasPendientes)
    const canRegisterPayment =
        totalPorPagar > 0 &&
        [RoleNames.admin, RoleNames.superadmin, RoleNames.recepcionista].map(String).includes(rolName)

    const tipoAlojamiento = data?.habitacion?.ultima_renta?.tarifas?.[0]?.tipo_alojamiento || ""
    const experiencias = data?.habitacion?.ultima_renta?.reserva_id
        ? data?.habitacion?.ultima_reserva?.reserva?.experiencias_reserva || []
        : []

    //Se agregó un filtro para limpiar los elementos pago_id repetidos
    const pagos = removeObjectDuplicates([...(data?.habitacion?.ultima_renta?.pagos || []), ...(reserva?.pagos || [])], "pago_id")

    const pagoEstancia = (data?.habitacion?.ultima_renta?.pagos || []).find(
        (p) => p.detalles_pago?.some((d) => d.origen_orden === OrigenOrden.Renta)
    )

    const propinasEstancia = (data?.habitacion?.ultima_renta?.propinas ?? []).reduce(
        (total, p) => total + (p?.total || 0),
        0
    )
    const propinasRS = (data?.habitacion?.ultima_renta?.ordenes ?? []).reduce(
        (total, orden) => total + (orden?.propina?.total || 0),
        0
    )

    const { descuentos, totalDescuento } = useDescuentos(data?.habitacion.ultima_renta)

    useEffect(() => {
        setOrdenesPendientesState(ordenesPendientes)

        setTotalPagado(
            sum([
                ...(pagos?.map((p) => p.total) || []),
                propinasEstancia,
                totalPropinasOrdenes,
            ])
        )
    }, [data])

    const getPagoEstanciaReserva = () => {
        const detalles_pago: any[] = []
        let total = 0

        pagos.forEach((d) => {
            detalles_pago.push(d?.detalles_pago || [])
            total = add(total, Number(d?.total || 0))
        })

        const pagoReservaEnRenta = (data?.habitacion?.ultima_renta?.pagos || []).find(
            (p) => p.detalles_pago?.some((d) => d.origen_orden === OrigenOrden.Reserva)
        )

        const ultimoPagoReserva = !pagoReservaEnRenta
            ? [...(reserva?.pagos || [])].sort(
                (a, b) =>
                    new Date(b.fecha_pago ?? 0).getTime() -
                    new Date(a.fecha_pago ?? 0).getTime()
            )[0]
            : pagoEstancia

        const pagoReservaSeleccionado = pagoReservaEnRenta ?? ultimoPagoReserva

        return {
            detalles_pago: detalles_pago.flat(1),
            fecha_pago: pagoReservaSeleccionado?.fecha_pago || "",
            pago_id: pagoReservaSeleccionado?.pago_id || "",
            extras: [],
            total,
        }
    }

    return (
        <div
            className={cx({
                "animante__opacity-transform__ease": true,
                "room-detail--occupied__tab--payments--main": true,
                "room-detail--occupied__tab--payments__box": true,
                "room-detail--occupied__tab--payments__box--no-button": !canRegisterPayment,
            })}
        >
            <div className="room-detail--occupied__tab--payments__list">
                <DrawerAccordion
                    title="Pagos registrados"
                    isEmpty={totalPagado <= 0}
                    emptyDescription="No hay pagos registrados"
                    emptyIcon={"CoinsFill"}
                >
                    <EstanciaDetailList
                        tarifas={data?.habitacion.ultima_renta?.rentas_tarifas || []}
                        ultima_renta={data?.habitacion.ultima_renta as Renta}
                        tipoAlojamiento={tipoAlojamiento as TiposAlojamientos}
                        pago={(reserva ? getPagoEstanciaReserva() : pagoEstancia) as Pago}
                    />
                    <ExtraDetailList
                        tipo_hospedaje={data?.habitacion.ultima_renta?.tipo_alojamiento || TiposAlojamientos.Hotel}
                        extras={data?.habitacion.ultima_renta?.extras || []}
                        fechas_renta={{
                            fecha_condensada: data?.habitacion.ultima_renta?.fecha_condensada,
                            fecha_registro: data?.habitacion.ultima_renta?.fecha_registro
                        }}
                        pagos={pagos}
                        reservas_tarifas={data?.habitacion?.ultima_renta?.reserva?.reservas_tarifas || []}
                    />
                    {experiencias.length > 0 && (
                        <ItemMultiplePayment
                            icon={"star"}
                            label="Experiencias"
                            className="detalle-h-items__multiple--experience"
                            payments={experiencias.map((exp) => ({
                                label: exp.experiencia?.nombre || "Experiencia",
                                amount: exp.total || 0,
                            }))}
                            showAmounts
                        />
                    )}

                    {/* Pagos de Room Service */}
                    {data?.habitacion?.ultima_renta?.ordenes
                        ?.filter(
                            (orden) =>
                                (orden.pago_id || (orden.pagos && orden.pagos.length > 0)) &&
                                orden.estado_orden !== EstadosOrdenHistorial.Cancelada
                        )
                        .map((o) => {
                            //  total  pagado (parcial o total)
                            const totalPagadoParcial =
                                o.pagos?.reduce((acc, p) => {
                                    const total = p.detalles_pago?.reduce((sum, d) => sum + (d.subtotal || 0), 0)
                                    return acc + (total || 0)
                                }, 0) || 0

                            const montoMostrar = o.pago_id ? o.total_con_iva || 0 : totalPagadoParcial

                            //fecha del último pago
                            const fechasPagos: string[] = []
                            if (o.pago?.fecha_pago) fechasPagos.push(o.pago.fecha_pago)
                            if (o.pagos?.length) {
                                o.pagos.forEach((p) => p.fecha_pago && fechasPagos.push(p.fecha_pago))
                            }

                            const ultimaFechaPago =
                                fechasPagos.length > 0
                                    ? new Date(Math.max(...fechasPagos.map((f) => new Date(f).getTime())))
                                    : null

                            const allDetallesPago =
                                o.pago?.detalles_pago && o.pago.detalles_pago.length > 0
                                    ? o.pago.detalles_pago
                                    : o.pagos?.flatMap((p) => p.detalles_pago || []) || []

                            const pagosAgrupados = o.pagos || []
                            const metodosAgrupados: string[] = []

                            pagosAgrupados.forEach((p) => {
                                const tiposUnicos = Array.from(new Set(p.detalles_pago?.map((d) => d.tipo_pago)))

                                if (tiposUnicos.length > 1) {
                                    //Pago mixto
                                    metodosAgrupados.push("Pago mixto")
                                } else if (tiposUnicos.length === 1) {
                                    const tipo = tiposUnicos[0]
                                    if (tipo === "love_points") {
                                        metodosAgrupados.push("Love points")
                                    } else {
                                        const d = p.detalles_pago?.[0]
                                        metodosAgrupados.push(getCardLabel(d, false))
                                    }
                                }
                            })

                            if (metodosAgrupados.length === 0 && allDetallesPago.length > 0) {
                                const tiposUnicos = Array.from(new Set(allDetallesPago.map((d) => d.tipo_pago)))
                                if (tiposUnicos.length > 1) {
                                    metodosAgrupados.push("Pago mixto")
                                } else {
                                    const tipo = tiposUnicos[0]
                                    if (tipo === "love_points") {
                                        metodosAgrupados.push("Love points")
                                    } else {
                                        const d = allDetallesPago[0]
                                        metodosAgrupados.push(getCardLabel(d, false))
                                    }
                                }
                            }

                            const metodosPagoFinal = Array.from(new Set(metodosAgrupados))

                            return (
                                <ItemPayment
                                    key={o.orden_id}
                                    withPrinter={false}
                                    icon="roomServiceCommand"
                                    label="Room Service"
                                    value={o.orden}
                                    amount={montoMostrar}
                                    payments={metodosPagoFinal}
                                    dateBottom
                                    printBottom
                                    dateBottomText={
                                        ultimaFechaPago
                                            ? formatCustomDate(
                                                UTCStringToLocalDate(ultimaFechaPago.toISOString()),
                                                "D MMM YYYY, hh:mm a"
                                            )
                                            : "-"
                                    }
                                />
                            )
                        })}

                    {(propinasRS > 0 || propinasEstancia > 0) && (
                        <PropinaDetails
                            payments={[
                                ...(propinasEstancia > 0
                                    ? [
                                        {
                                            label: "Estancia",
                                            amount: propinasEstancia,
                                            date: pagoEstancia?.fecha_pago
                                                ? formatCustomDate(
                                                    UTCStringToLocalDate(
                                                        pagoEstancia?.fecha_pago
                                                    ),
                                                    "D MMM YYYY, hh:mm a"
                                                )
                                                : "",
                                        },
                                    ]
                                    : []),

                                ...(data?.habitacion?.ultima_renta?.ordenes
                                    ?.filter((o) => typeof o.propina?.total === "number" && o.propina.total > 0)
                                    .map((o) => ({
                                        label: "Room Service",
                                        amount: o.propina?.total || 0,
                                        date: o.fecha_registro
                                            ? formatCustomDate(
                                                UTCStringToLocalDate(o.fecha_registro),
                                                "D MMM YYYY, hh:mm a"
                                            )
                                            : "",
                                    })) || []),
                            ]}
                        />
                    )}
                </DrawerAccordion>

                <DrawerAccordion
                    title="Pagos pendientes"
                    isEmpty={totalPorPagar <= 0}
                    emptyDescription="No hay pagos pendientes"
                    emptyIcon={"coinsFill"}
                >
                    {
                        estanciaPendiente > 0 && (
                            <PagoPendienteDetails
                                date={formatCustomDate(
                                    UTCStringToLocalDate(data?.habitacion.ultima_renta?.fecha_registro),
                                    "D MMM YYYY, hh:mm a"
                                )}
                                iconName="HotelFilled"
                                onPrint={() => handlePrint(data?.habitacion?.ultima_renta?.renta_id || "", "custom", "4")}
                                payments={[{
                                    label: String(
                                        data?.habitacion.ultima_renta?.conceptos_pendientes?.estancia_pendiente?.cantidad
                                        ?? data?.habitacion.ultima_renta?.rentas_tarifas?.reduce((acc, rt) => acc + (rt.subtotales?.noches || 0), 0)
                                        ?? calcularEstanciasBase(data?.habitacion.ultima_renta?.tarifas || [], data?.habitacion.ultima_renta?.fecha_registro, data?.habitacion.ultima_renta?.fecha_fin)
                                    ),
                                    amount: estanciaPendiente || 0
                                }]}
                                title="Estancia"
                            />
                        )
                    }
                    {data?.habitacion?.ultima_renta?.extras
                        ?.filter((extra) => !extra.fecha_pago)
                        .map((extra, index) => (
                            (extra.extras_tarifas || []).map((et, t_index) => (
                                <PagoPendienteDetails
                                    key={`${index}-${t_index}`}
                                    date={formatCustomDate(
                                        UTCStringToLocalDate(extra.fecha_solicitud),
                                        "D MMM YYYY, hh:mm a"
                                    )}
                                    iconName={mapTiposExtrasIcon[extra?.tipo_extra || ""]}
                                    onPrint={() => handlePrint(extra?.extra_id || "", "custom", "5")}
                                    payments={[{
                                        amount: et.subtotales?.total || 0,
                                        label: String(extra.numero || 0)
                                    }]}
                                    title={extra.tipo_extra === TIPOS_EXTRAS.hospedaje
                                        ? tipoAlojamiento === "motel" ? "Hospedaje extra" : "Noches extras"
                                        : mapTiposExtrasTitulo[extra?.tipo_extra || ""]}
                                />
                            ))
                        ))}

                    {ordenesPendientesState.length > 0 &&
                        ordenesPendientesState.map((orden, index) => {

                            return (
                                <>
                                    <PagoPendienteDetails
                                        key={index}
                                        date={
                                            orden?.fecha_registro
                                                ? formatCustomDate(
                                                    UTCStringToLocalDate(orden.fecha_registro),
                                                    "D MMM YYYY, hh:mm a"
                                                )
                                                : "-"
                                        }
                                        iconName="roomServiceCommand"
                                        payments={[{
                                            amount: orden.total_con_iva,
                                            label: orden.orden
                                        }]}
                                        title="Room Service"
                                    />
                                </>
                            )
                        })}
                </DrawerAccordion>

                <DrawerAccordion
                    title="Descuentos"
                    isEmpty={!(descuentos.length > 0)}
                    emptyDescription="No hay descuentos registrados"
                    emptyIcon={"priceTag"}
                >
                    {descuentos.map((descuento, index) => (
                        <DescuentoDetails
                            key={index}
                            monto={descuento.monto}
                            fecha={descuento.fecha}
                            nombre={descuento.nombre}
                            tipo={descuento.tipo}
                        />
                    ))}
                </DrawerAccordion>
            </div>

            <div className="room-detail--occupied__tab--payments__footer">
                <div className="room-detail--occupied__tab--payments__divider"></div>
                {totalDescuento > 0 &&
                    <div className="room-detail__tab--payments__group">
                        <span className="room-detail__tab--payments--thin">Descuentos</span>
                        <span className="room-detail__tab--payments--bold">-{formatCurrency(totalDescuento)}</span>
                    </div>
                }
                <div className="room-detail__tab--payments__group">
                    <span className="room-detail__tab--payments--thin">Total pagado</span>
                    <span className="room-detail__tab--payments--bold">{formatCurrency(totalPagado)}</span>
                </div>
                <div className="room-detail__tab--payments__group">
                    <span className="room-detail__tab--payments--thin">Por pagar</span>
                    <span className="room-detail__tab--payments--bold">{formatCurrency(Math.max(0, (totalPorPagar || 0)))}</span>
                </div>
            </div>

            {canRegisterPayment && (
                <PrimaryButton
                    text={"Registrar pago"}
                    disabled={
                        rolName === RoleNames.roomService
                            ? ordenesPendientesState.length === 0 || totalPorPagar === 0
                            : totalPorPagar === 0
                    }
                    onClick={validateIsColabActive(() => {
                        navigate(`/u/room-service/pago/${room.ultima_renta.renta_id}`, {
                            state: { origen: "Estancia" },
                        })
                    })}
                    style={{ marginBottom: "12px", position: "relative", bottom: 0 }}
                />
            )}
            {InactiveModal}
        </div>
    )
}

export default Payments


